import { useState, useEffect, useCallback } from 'react';
import type { InventoryHistory } from '@shared/types';
import type { InventorySession, InventorySessionsResponse, InventoryHistoryResponse } from '@shared/api-responses';
import { useStations } from '../hooks/useStations';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../hooks/useApi';
import Modal from '../components/Modal';

export default function Inventories() {
  const { stations } = useStations();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [sessions, setSessions] = useState<InventorySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stationFilter, setStationFilter] = useState<number | null>(null);
  const [expandedSession, setExpandedSession] = useState<number | null>(null);
  const [sessionItems, setSessionItems] = useState<Record<number, InventoryHistory[]>>({});
  const [itemsLoading, setItemsLoading] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<InventorySession | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Fetch sessions
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (stationFilter) params.set('stationId', String(stationFilter));

    setError(null);
    apiFetch<InventorySessionsResponse>(`/inventory/sessions${params.toString() ? `?${params}` : ''}`)
      .then((data) => setSessions(data.sessions))
      .catch((err) => {
        setSessions([]);
        setError(err instanceof Error ? err.message : 'Failed to load sessions');
      })
      .finally(() => setLoading(false));
  }, [stationFilter, reloadKey]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiFetch(`/inventory/sessions/${deleteTarget.id}`, { method: 'DELETE' });
      // Drop any cached items for the removed session
      setSessionItems((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      if (expandedSession === deleteTarget.id) setExpandedSession(null);
      setDeleteTarget(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete session');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, expandedSession]);

  // Toggle session expansion and lazy-load items
  const toggleSession = useCallback(
    async (sessionId: number) => {
      if (expandedSession === sessionId) {
        setExpandedSession(null);
        return;
      }

      setExpandedSession(sessionId);

      if (!sessionItems[sessionId]) {
        setItemsLoading(sessionId);
        try {
          const data = await apiFetch<InventoryHistoryResponse>(`/inventory/history?sessionId=${sessionId}`);
          setSessionItems((prev) => ({ ...prev, [sessionId]: data.history }));
        } catch {
          setSessionItems((prev) => ({ ...prev, [sessionId]: [] }));
        } finally {
          setItemsLoading(null);
        }
      }
    },
    [expandedSession, sessionItems],
  );

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'short':
        return 'bg-ems-red/15 text-ems-red border border-ems-red/20';
      case 'over':
        return 'bg-blue-500/15 text-blue-400 border border-blue-500/20';
      case 'good':
        return 'bg-ems-green/15 text-ems-green border border-ems-green/20';
      default:
        return 'bg-zinc-800 text-zinc-400 border border-zinc-700';
    }
  };

  return (
    <div className="min-h-dvh bg-surface text-white pb-20 md:pb-6">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border-subtle">
        <h1 className="text-xl font-bold">Inventory History</h1>
        <p className="text-zinc-500 text-sm mt-0.5">Past completed inventory sessions</p>
      </div>

      {/* Station filter */}
      <div className="px-4 py-3 border-b border-border-subtle">
        <select
          value={stationFilter ?? ''}
          onChange={(e) => setStationFilter(e.target.value ? Number(e.target.value) : null)}
          className="w-full rounded-xl bg-surface-raised border border-border-subtle px-3 py-2.5 text-white text-sm focus:border-dcvfd-accent focus:outline-none transition-colors"
        >
          <option value="">All Stations</option>
          {stations.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="px-4 py-4 md:max-w-4xl md:mx-auto">
        {error && (
          <div className="mb-4 rounded-xl bg-red-950/50 border border-red-900/50 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-dcvfd-accent border-t-transparent rounded-full" />
          </div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-14 w-14 rounded-2xl bg-surface-raised border border-border-subtle flex items-center justify-center mb-4">
              <svg className="h-7 w-7 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <p className="text-zinc-400 font-medium">No inventories yet</p>
            <p className="text-zinc-600 text-sm mt-1">Completed inventory sessions will appear here</p>
          </div>
        )}

        {!loading && sessions.length > 0 && (
          <div className="space-y-3">
            {sessions.map((session) => {
              const isExpanded = expandedSession === session.id;
              const items = sessionItems[session.id];
              const isLoadingItems = itemsLoading === session.id;

              return (
                <div
                  key={session.id}
                  className="rounded-2xl bg-surface-raised border border-border-subtle overflow-hidden"
                >
                  {/* Session row — clickable */}
                  <button
                    type="button"
                    onClick={() => toggleSession(session.id)}
                    className="w-full text-left px-4 py-3 hover:bg-surface-overlay transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{session.station_name}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {formatDate(session.submitted_at)} at {formatTime(session.submitted_at)}
                        </p>
                        {session.submitted_by && (
                          <p className="text-xs text-zinc-600 mt-0.5">by {session.submitted_by}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <div className="text-right">
                          <p className="text-xs text-zinc-500">
                            {session.item_count} item{session.item_count !== 1 ? 's' : ''}
                          </p>
                          {session.items_short > 0 ? (
                            <p className="text-xs font-medium text-ems-red mt-0.5">{session.items_short} short</p>
                          ) : (
                            <p className="text-xs font-medium text-ems-green mt-0.5">All good</p>
                          )}
                        </div>
                        <svg
                          className={`h-4 w-4 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-border-subtle px-4 py-3">
                      {isLoadingItems && (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin h-5 w-5 border-2 border-dcvfd-accent border-t-transparent rounded-full" />
                        </div>
                      )}

                      {!isLoadingItems && items && items.length === 0 && (
                        <p className="text-sm text-zinc-500 text-center py-3">No item records found</p>
                      )}

                      {!isLoadingItems && items && items.length > 0 && (
                        <div className="space-y-1">
                          {/* Group items by category */}
                          {(() => {
                            const byCategory = new Map<string, InventoryHistory[]>();
                            for (const item of items) {
                              const group = byCategory.get(item.category) ?? [];
                              group.push(item);
                              byCategory.set(item.category, group);
                            }

                            return Array.from(byCategory.entries()).map(([category, catItems]) => (
                              <div key={category} className="mb-3">
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
                                  {category}
                                </p>
                                <div className="space-y-1">
                                  {catItems.map((item) => (
                                    <div
                                      key={item.id}
                                      className="flex items-center justify-between rounded-lg bg-surface px-3 py-2"
                                    >
                                      <span className="text-sm text-zinc-300 truncate mr-2">{item.item_name}</span>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-xs text-zinc-500 font-mono">
                                          {item.actual_count}/{item.target_count}
                                        </span>
                                        <span
                                          className={`rounded-md px-2 py-0.5 text-xs font-mono font-bold ${statusColor(item.status)}`}
                                        >
                                          {item.delta > 0 ? `+${item.delta}` : item.delta}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}

                      {isAdmin && (
                        <div className="mt-3 pt-3 border-t border-border-subtle flex justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteError(null);
                              setDeleteTarget(session);
                            }}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-ems-red/15 hover:bg-ems-red/25 border border-ems-red/30 px-3 py-2 text-xs font-medium text-ems-red transition-colors active:scale-[0.98]"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"
                              />
                            </svg>
                            Delete session
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal open={deleteTarget !== null} onClose={() => (deleting ? undefined : setDeleteTarget(null))}>
        {deleteTarget && (
          <div>
            <div className="text-center mb-4">
              <div className="mx-auto h-12 w-12 rounded-full bg-ems-red/20 flex items-center justify-center mb-3">
                <svg className="h-6 w-6 text-ems-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-white">Delete this inventory?</h2>
            </div>
            <div className="rounded-xl bg-surface px-3 py-2.5 mb-4 text-sm">
              <p className="text-zinc-200 font-medium">{deleteTarget.station_name}</p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {formatDate(deleteTarget.submitted_at)} at {formatTime(deleteTarget.submitted_at)}
                {deleteTarget.submitted_by ? ` · ${deleteTarget.submitted_by}` : ''}
              </p>
            </div>
            <p className="text-sm text-zinc-400 text-center mb-5">
              The session will be hidden from dashboards and history. Any auto-generated resupply order will also be
              removed. Records are kept in the database for audit.
            </p>
            {deleteError && (
              <p className="text-xs text-ems-red text-center mb-3" role="alert">
                {deleteError}
              </p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="flex-1 rounded-xl bg-surface-overlay border border-border-subtle px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98] transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 rounded-xl bg-ems-red hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2.5 text-sm font-medium text-white active:scale-[0.98] transition-all"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
